import {
  Component,
  DestroyRef,
  inject,
  Renderer2,
  RendererFactory2,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { Router, RouterModule, RouterOutlet } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { OAuthErrorEvent, OAuthService } from 'angular-oauth2-oidc';
import { JwksValidationHandler } from 'angular-oauth2-oidc-jwks';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { CardModule } from 'primeng/card';
import { ChartModule } from 'primeng/chart';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { ScrollTopModule } from 'primeng/scrolltop';
import { SkeletonModule } from 'primeng/skeleton';
import { ToastModule } from 'primeng/toast';
import { environment } from '../environments/environment';
import { currentVersion } from './appVersion';
import { authConfig } from './auth/auth-config';
import { ClientPortalService } from './services/clientPortalService/client-portal.service';
import { UserDetailsAPIPayload } from './services/clientPortalService/types/types';
import { ClientWarehouseDropdownComponent } from './shared/components/client-warehouse-dropdown/client-warehouse-dropdown.component';
import { FeedbackFormComponent } from './shared/components/feedback-form/feedback-form.component';
import { SidebarComponent } from './shared/components/sidebar/sidebar.component';
import {
  CURRENT_ENTITY,
  DEFAULT_ENTITY,
  DOMAIN_NAME,
  ENTITY_VARS,
} from './shared/constants/entity-vars';
import { FaviconService } from './shared/services/favicon.service';
import { SidebarService } from './shared/services/sidebar.service';
import { SideBarSkeletonComponent } from './shared/skeleton/side-bar-skeleton/side-bar-skeleton.component';
const jwtHelper = new JwtHelperService();
// Branding that is already known from the hostname. The bpost domains resolve
// their entity from the userDetails API instead, so nothing can be applied early.
const HOSTNAME_ENTITY_VAR =
  CURRENT_ENTITY === 'bpost' || CURRENT_ENTITY === 'bpost_prod'
    ? null
    : ENTITY_VARS[CURRENT_ENTITY];
@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    ButtonModule,
    SkeletonModule,
    CardModule,
    OverlayPanelModule,
    ClientWarehouseDropdownComponent,
    ToastModule,
    RouterModule,
    SidebarComponent,
    SideBarSkeletonComponent,
    FeedbackFormComponent,
    ScrollTopModule,
    ChartModule,
    CalendarModule,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent {
  title = 'rcp-frontend-v2';
  oAuthService = inject(OAuthService);
  cpService = inject(ClientPortalService);
  titleService = inject(Title);
  faviconService = inject(FaviconService);
  router = inject(Router);
  destroyRef = inject(DestroyRef);
  location = window.location;
  loggedInUserInfo: any;
  showHeader = false;
  userDetails: any;
  sitesFetched = false;
  userDetailsFetched = false;
  redirectToDashboard = 0;
  //constants used in html
  welcome = 'Welcome, ';
  backToTop = 'Back to Top';
  branchName = '';
  branchURL = '';
  appversion = currentVersion;
  appTitle = 'Radial Client Portal';
  logoName = HOSTNAME_ENTITY_VAR?.logoName ?? null;
  userProfile: any;
  selectedEntityVar: any;
  renderer: Renderer2;
  constructor(
    public sidebarService: SidebarService,
    rendererFactory: RendererFactory2,
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
    this.applyHostnameBranding();
    this.initOAuth();
    this.addDeployedBranchInfo();
  }
  initOAuth(): void {
    this.oAuthService.configure(authConfig);
    this.oAuthService.setStorage(localStorage);
    this.oAuthService.tokenValidationHandler = new JwksValidationHandler();
    this.oAuthService.loadDiscoveryDocumentAndTryLogin().then(
      (isLoggedIn) => {
        if (this.oAuthService.hasValidAccessToken()) {
          this.initAppFlow();
        } else {
          this.oAuthService.initCodeFlow();
        }
      },
      (e: OAuthErrorEvent) => {
        console.error(e.reason, e.params, 'error');
        this.error(e);
      },
    );
  }
  initAppFlow(): void {
    this.oAuthService.loadUserProfile().then(
      (userInfo: { info: any }) => {
        const { companyName, entryUUID, firstName, lastName, mail, sub } =
          userInfo.info;
        this.loggedInUserInfo = {
          companyName,
          entryUUID,
          firstName,
          lastName,
          mail,
          sub,
        };
        this.getWareHousesList();
        this.getUserDetails();
      },
      (e) => {
        this.error(e);
      },
    );
  }
  /**
   * Applies the branding known from the hostname so the favicon is set before
   * login. loadEntityBasedProperty() stays authoritative once userDetails returns.
   */
  applyHostnameBranding(): void {
    if (HOSTNAME_ENTITY_VAR?.icon) {
      this.faviconService.setFavicon(HOSTNAME_ENTITY_VAR.icon);
    }
  }
  /**
   * Falls back to DEFAULT_ENTITY when the currentEntity value is null or unknown,
   * so the portal still renders branding rather than undefined.
   */
  resolveEntityVar(apiEntity: string, currentEntity = CURRENT_ENTITY) {
    const key =
      currentEntity === 'bpost' || currentEntity === 'bpost_prod'
        ? apiEntity?.trim()?.toLowerCase()
        : currentEntity;
    const entityVar = ENTITY_VARS[key];
    if (!entityVar) {
      console.warn(
        `entity "${apiEntity}" did not resolve for host "${DOMAIN_NAME}"; falling back to "${DEFAULT_ENTITY}"`,
      );
      return ENTITY_VARS[DEFAULT_ENTITY];
    }
    return entityVar;
  }
  loadEntityBasedProperty() {
    console.log("user entity", this.userDetails[0]?.entity);
    this.selectedEntityVar = this.resolveEntityVar(this.userDetails[0]?.entity);
    this.logoName = this.selectedEntityVar?.logoName;
    this.updatePageTitle(this.selectedEntityVar?.title);
    this.faviconService.setFavicon(this.selectedEntityVar?.icon);
  }
  getWareHousesList() {
    this.cpService.getWarehouses().subscribe({
      next: (data: any) => {
        const response = data?.response;
        this.cpService.allClientWareHouses$.next(response);
      },
      error: (e) => {
        this.error(e);
      },
      complete: () => {
        this.sitesFetched = true;
      },
    });
  }
  getUserDetails() {
    const UUID =
      this.loggedInUserInfo?.entryUUID ??
      this.loggedInUserInfo.mail ??
      this.loggedInUserInfo.sub;
    const payLoad: UserDetailsAPIPayload = {
      company_name: '',
      uuid: UUID,
      search_uuid: UUID,
      portal:
        CURRENT_ENTITY === 'bpost_prod_internal' ||
        CURRENT_ENTITY === 'bpost_internal'
          ? 'wiam'
          : 'ciam',
    };
    this.showHeader = false;
    this.cpService
      .getUserDetails(payLoad)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: any) => {
          this.userDetails = data.response;
          if (data.statusmessage === 'Success' && this.userDetails.length > 0) {
            this.storeLoggedInUserInfo(
              this.userDetails[0].uuid,
              this.userDetails[0].role_name,
              this.userDetails[0].role_acronym,
            );
            this.cpService.processUserDetailsAPIResponse(this.userDetails);
            this.validateClientSession(
              this.userDetails[0].client_acronym.trim(),
            );
            sessionStorage.setItem(
              'companyname',
              this.userDetails[0].client_acronym.trim(),
            );
            this.cpService.logInUserDetails$.next(data.response);
            this.loadEntityBasedProperty();
          } else if (
            (data.response.errorCode === 'Err_1006' &&
              data.statusmessage === 'Fail') ||
            data.response.length === 0
          ) {
            this.router.navigate(['no-access']);
          }
          this.showHeader = true;
        },
        error: (e) => {
          console.log('error while calling user details api ', e);
          this.error(e);
        },
        complete: () => {
          this.userDetailsFetched = true;
        },
      });
  }
  storeLoggedInUserInfo(UUID: string, rol: string, roleAcnym: string): void {
    this.loggedInUserInfo.UUID = UUID;
    this.loggedInUserInfo.role = rol;
    this.loggedInUserInfo.role_acc = roleAcnym;
    sessionStorage.setItem('uuid', UUID);
    this.cpService.setLoggedInUserInfo(this.loggedInUserInfo);
  }
  onClientWarehouseChange(e: {
    clientName: string;
    warehouseList: string[];
    warehouseNames: string[];
  }) {
    const { clientName: newClient, warehouseList, warehouseNames } = e;
    console.log('warehouseList', warehouseList);
    this.validateClientSession(newClient);
    sessionStorage.setItem('companyname', newClient);
    sessionStorage.setItem('selectedWareHouses', warehouseList.toString());
    this.cpService.currentSelectedClient$.next(newClient);
    this.cpService.currentlySelectedWarehouses$.next(warehouseList);
    this.cpService.currentlySelectedWarehouseNames$.next(warehouseNames);
  }
  error(e: any, forceLogout = true) {
    console.error('error occured ', e);
    this.logOut();
    this.router.navigate(['no-access']);
    this.showHeader = true;
  }
  revokeTokenNlogOut() {
    this.oAuthService.revokeTokenAndLogout();
    sessionStorage.clear();
  }
  logOut() {
    this.oAuthService.logOut();
  }
  navigateToHomePage() {
    const routeUrl = this.cpService.getFirstRouteByActiveClient();
    if (routeUrl !== '') {
      this.router.navigate([routeUrl]);
    } else {
      console.error('unable to get home url for active client');
    }
  }
  /**
   * below function will remove session storage data except uuid when ever stored client does not
   * matched with active client (i.e first client we are getting from userDetails api)
   */
  validateClientSession(client: string): void {
    const storedClient = sessionStorage.getItem('companyname') || '';
    if (storedClient !== client) {
      console.warn(
        `session storage is cleared as new client "${client}" is different from old client "${storedClient}"`,
      );
      const keysToRemove = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const sessionKey = sessionStorage.key(i);
        if (sessionKey !== 'uuid') {
          keysToRemove.push(sessionKey);
        }
      }
      keysToRemove.forEach((key) => {
        sessionStorage.removeItem(key);
      });
    }
  }
  addDeployedBranchInfo() {
    if (!environment.production) {
      this.branchName = environment.DEPLOYED_BRANCH_NAME;
      this.branchURL = environment.DEPLOYED_BRANCH_URL;
    }
  }
  updatePageTitle(title: string) {
    this.appTitle = title;
    this.titleService.setTitle(title);
  }
}
